import React, { Component } from 'react'
import PropTypes from 'prop-types'
import uuid from 'uuid'
import { withRouter } from 'react-router'
import { push } from 'connected-react-router'
import { get } from 'lodash'
import { connect } from 'react-redux'
import {
  persons as personsApi,
  contacts as contactsApi,
  participants as participantsApi,
  leaders as leadersApi,
  assessors as assessorsApi
} from 'redux/modules/api'
import {
  notification,
  modal,
  changeLocale,
  onRefreshPage
} from 'redux/modules/app'
import { saveUser } from 'redux/modules/auth'
import { isTopUser as isTopUserFunc } from 'redux/selectors/auth'
import {
  updateSchema,
  roleSchema,
  deleteRoleSchema,
  editRoleSchema,
  changePasswordSchema
} from 'models/persons'
import { personId as getPersonId } from 'utils/token'
import appConfig from 'config'
import { routerAdapter } from 'helpers/decorators'
import { compose } from 'recompose'
import infoRoleSchema from '../../../../models/persons/infoRoleSchema'

function decorator(config = {}) {
  return ComposedComponent => {
    const enhance = compose(
    routerAdapter(),
    withRouter,
    connect(
      state => ({
        persons: state.api.persons,
        activeOrganization: state.auth.organization,
        user: state.auth.user,
        local: {
          contacts: state.api.contacts
        },
        isTopUser: isTopUserFunc(state)
      }),
      {
        ...personsApi,
        notification,
        changeLocale,
        modal,
        saveUser,
        onRefreshPage,
        getOrganizationContact: contactsApi.getOrganizationContact,
        getAssessor: assessorsApi.getAssessorDetail,
        getParticipant: participantsApi.getParticipant,
        getLeader: leadersApi.getLeader,
        push
      }
    )
    )
    class Persons extends Component {
      static propTypes = {
        local: PropTypes.object.isRequired,
        isTopUser: PropTypes.bool,
        activeOrganization: PropTypes.object.isRequired,
        persons: PropTypes.object.isRequired,
        notification: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired,
        modal: PropTypes.func.isRequired,
        changeLocale: PropTypes.func.isRequired,
        getProfile: PropTypes.func.isRequired,
        changePersonPassword: PropTypes.func.isRequired,
        deactivateAccount: PropTypes.func.isRequired,
        getLocales: PropTypes.func.isRequired,
        removeDetailData: PropTypes.func.isRequired,
        updatePerson: PropTypes.func.isRequired,
        uploadPersonImage: PropTypes.func.isRequired,
        deletePersonImage: PropTypes.func.isRequired,
        addPersonRole: PropTypes.func.isRequired,
        editPersonRole: PropTypes.func.isRequired,
        getPersonRoles: PropTypes.func.isRequired,
        getPersonUserTypes: PropTypes.func.isRequired,
        getPersonEmailPrefs: PropTypes.func.isRequired,
        sendPersonEmail: PropTypes.func.isRequired,
        removePersonRole: PropTypes.func.isRequired,
        getInfoAboutRole: PropTypes.func.isRequired,
        getOrganizationContact: PropTypes.func.isRequired,
        getAssessor: PropTypes.func.isRequired,
        getParticipant: PropTypes.func.isRequired,
        changePersonLogin: PropTypes.func.isRequired,
        getLeader: PropTypes.func.isRequired,
        saveUser: PropTypes.func.isRequired,
        onRefreshPage: PropTypes.func.isRequired,
        personHocConfig: PropTypes.object,
        user: PropTypes.object,
        decoPersonUpdate: PropTypes.string
      }

      static defaultProps = {
        personHocConfig: {}
      }

      constructor(props) {
        super(props)
        this.key = uuid.v1()
      }

      componentDidMount = () => {
        const { isTopUser } = this.props

        config.detail && this.handleGetProfile()

        config.locales &&
          this.props.getLocales(isTopUser)
      }

      componentWillUnmount = () => this.props.removeDetailData()

      handleGetProfile = () =>
        this.props.getProfile({ readParams: config.readParams })

      handlePersonUpdate = (fields, values) => {
        if (fields.file) {
          this.props
            .uploadPersonImage(fields.id, fields.file)
            .then(({ body = {} }) => {
              this.handleUpdate(fields.id, updateSchema(fields), {
                values,
                photoUrl: body.photoUrl
              })
            })
            .catch(this.handleError)

          return
        }

        if (get(this.props.persons, 'detail.photoUrl') && !fields.photoUrl) {
          this.props.deletePersonImage(fields.id).then(() => {
            this.handleUpdate(fields.id, updateSchema(fields), { values })
          })
          return
        }

        this.handleUpdate(fields.id, updateSchema(fields), { values })
      }

      handleGetPersonRoles = id => {
        const { params } = this.props

        this.props.getPersonRoles(id || params.contactId)
      }

      handleChangePersonLogin = (id, fields, { isSelf } = {}) =>
        this.props
          .changePersonLogin(id, {
            value: fields.email
          })
          .then(() => this.handleChangeLoginSuccess({ isSelf }))
          .catch(this.handleError)

      handleChangePersonPassword = (id, fields) => {
        this.props
          .changePersonPassword(id, changePasswordSchema(fields))
          .then(this.handlePasswordChangeSuccess)
          .catch(this.handleError)
      }

      handleChangePersonState = (fields, data) => {
        return this.props
          .updatePerson(data.id, fields, {
            updateParams: 'CHANGE_PERSON_STATE'
          }, data.awardLeaderId)
          .then(this.handlePersonStateChangeSuccess)
          .catch(this.handleError)
      }

      handleDeactivateAccount = (id, isActive) => {
        return this.props.deactivateAccount(id, isActive)
          .then(this.handlePersonStateChangeSuccess)
          .catch(this.handleError)
      }

      handleGetPersonEmailPrefs = personId =>
        this.props.getPersonEmailPrefs(personId).catch(this.handleError)

      handleSendPersonEmail = fields =>
        this.props
          .sendPersonEmail(fields)
          .then(() => {
            this.props.notification('Email has been sent', {
              status: 'success',
              intlKey: 'notifications.email_has_been_sent'
            })
            this.props.modal(null)
          })
          .catch(this.handleError)

      handleUpdatePersonUserTypes = id => {
        if (getPersonId(this.props.user) !== id) {
          return
        }

        this.props.getPersonUserTypes().then(({ body } = {}) => {
          const userTypes =
            get(body, 'userTypes') || get(this.props.user, 'userTypes')

          this.props
            .saveUser({
              ...this.props.user,
              ...userTypes
            })
            .catch(this.handleError)
        })
      }

      handleUpdate = (id, fields, options = {}) => {
        const { persons } = this.props
        const updateLocale = persons.locales.filter(
          item => item.id === get(fields, 'locale.id')
        )[0]

        this.props
          .updatePerson(id, fields)
          .then(() => {
            this.props.changeLocale(updateLocale)
            this.handleUpdateApp(fields, { ...options, locale: updateLocale })
          })
          .catch(this.handleError)
      }

      handleAddPersonRole = (id, fields, opts) =>
        this.props
          .addPersonRole(id, roleSchema(fields.organizationId, fields))
          .then(() =>
            this.handleChangePersonRole(id, {
              ...opts,
              intlKey: 'notifications.new_role_has_been_added',
              message: 'New role has been added'
            })
          )
          .catch(this.handleError)

      handleEditPersonRole = (id, roleToRemove, roleToAdd, opts) =>
        this.props
          .editPersonRole(id, editRoleSchema(roleToRemove, roleToAdd))
          .then(() =>
            this.handleChangePersonRole(id, {
              ...opts,
              intlKey: 'notifications.role_has_been_updated',
              message: 'New role has been updated'
            })
          )
          .catch(this.handleError)

      handleRemovePersonRole = (id, roleToDelete, opts) =>
        this.props
          .removePersonRole(id, deleteRoleSchema(roleToDelete, get(opts, 'shouldBeOrgContactDeleted')))
          .then(res => {
            this.handleChangePersonRole(id, {
              ...opts,
              intlKey: 'notifications.role_has_been_removed',
              message: 'Role has been removed',
              isContactDeleted: res.body.deleted,
              organizationId: res.body.contactOrgId
            })
          }
          )
          .catch(this.handleError)

      handleGetInfoAboutPersonRole = (id, data) => {
          return this.props.getInfoAboutRole(id, infoRoleSchema(data))
              .then(res => {
                    return res.body
                  }
              )
              .catch(this.handleError)
      }

      handleUpdateApp = (fields = {}, options = {}) => {
        const { user } = this.props
        const localeChange =
          get(fields, 'locale.id') !== get(options, 'values.locale.id')

        if (
          fields.firstName !== options.values.firstName ||
          fields.lastName !== options.values.lastName ||
          fields.locale.id !== get(options, 'locale.id') ||
          options.photoUrl !== options.values.photoUrl
        ) {
          this.props
            .saveUser({
              ...user,
              firstName: fields.firstName,
              lastName: fields.lastName,
              personLocale: get(options, 'locale.code'),
              photoUrl: options.photoUrl || fields.photoUrl
            })
            .then(() => {
              this.handleSubmit()
              if (localeChange) {
                this.props.onRefreshPage(true)
              }
            })
            .catch(this.handleError)

          return
        }
        this.handleSubmit()

        if (localeChange) {
          this.props.onRefreshPage(true)
        }
      }

      handleSubmit = () => {
        this.props.notification('Personal details have been updated', {
          status: 'success',
          intlKey: 'notifications.personal_details_have_been_updated'
        })
      }

      handleChangePersonRole = (id, opts = {}) => {
        this.props.notification(opts.message, {
          status: 'success',
          intlKey: opts.intlKey
        })

        if (opts.isOwnContact) {
          this.props.push(`${appConfig.publicPath}/logout`)
          return
        }

        if ((opts.isContactDeleted && opts.organizationId === this.props.activeOrganization.id) ||
          (opts.isContactDeleted && opts.organizationId !== this.props.activeOrganization.id && opts.roleCount === 1)) {
          this.props.push(`${appConfig.publicPath}/`)
          return
        }

        this.handleUpdatePersonUserTypes(id)
        this.handleRefresh()
      }

      handlePasswordChangeSuccess = () => {
        this.props.notification('Password has been change', {
          status: 'success',
          intlKey: 'notifications.password_has_been_changed'
        })
        this.handleRefresh()
      }

      handlePersonStateChangeSuccess = () => {
        this.props.notification('Person state has been changed', {
          status: 'success',
          intlKey: 'notifications.person_state_has_been_changed'
        })
        this.handleRefresh()
      }

      handleChangeLoginSuccess = ({ isSelf } = {}) => {
        this.props.notification('Login details have been changed', {
          status: 'success',
          intlKey: 'notifications.login_details_have_been_changed'
        })
        if (isSelf) {
          this.props.push(`${appConfig.publicPath}/logout`)
          return
        }
        this.handleRefresh()
      }

      handleError = ({ body }) =>
        this.props.notification(body && body.userMessage, 'error')

      handleGetOrganizationId = () =>
        get(this.props, 'params.organizationId') ||
        this.props.activeOrganization.id

      handleUpdateContact = () => {
        const { params } = this.props

        this.props.getOrganizationContact(
          get(params, 'organizationId'),
          get(params, 'contactId')
        )
      }

      handleRefresh = () => {
        const {
          params,
          local,
          activeOrganization,
          personHocConfig
        } = this.props

        this.props.modal(null)

        if (config.updateContact || config.updateContactRoles) {
          this.handleUpdateContact()
        }

        if (config.updateAssessor || personHocConfig.decoUpdateAssessor) {
          this.props.getAssessor({
            orgId: params.organizationId || activeOrganization.id,
            assessorId: params.assessorId
          })
        }

        if (
          config.updateContactRoles && get(local, 'contacts.detail.person.id')
        ) {
          this.handleGetPersonRoles(local.contacts.detail.person.id)
        }

        if (this.props.decoPersonUpdate === 'participantDetail') {
          this.props.getParticipant(get(params, 'id'))
        }

        if (this.props.decoPersonUpdate === 'leaderUpdate') {
          this.props.getLeader(get(params, 'id'), {
            readParams: config.leaderReadParams
          })
        }

        if (this.props.decoPersonUpdate === 'personUpdate') {
          this.props.getProfile({ readParams: config.readParams })
        }

        if (this.props.decoPersonUpdate === 'contactDetail') {
          this.handleUpdateContact()
        }
      }

      render() {
        return (
          <ComposedComponent
            {...this.props}
            key={this.key}
            onPersonUpdate={this.handlePersonUpdate}
            getProfile={this.handleGetProfile}
            addPersonRole={this.handleAddPersonRole}
            editPersonRole={this.handleEditPersonRole}
            deletePersonRole={this.handleRemovePersonRole}
            getInfoAboutPersonRole={this.handleGetInfoAboutPersonRole}
            getPersonRoles={this.handleGetPersonRoles}
            changePersonLogin={this.handleChangePersonLogin}
            changePersonPassword={this.handleChangePersonPassword}
            changePersonState={this.handleChangePersonState}
            onDeactivateAccount={this.handleDeactivateAccount}
            getPersonEmailPrefs={this.handleGetPersonEmailPrefs}
            sendPersonEmail={this.handleSendPersonEmail}
            local={undefined}
          />
        )
      }
    }
    return enhance(Persons)
  }
}

export default decorator
